'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Goal, H, Hl, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/ml'

const LineFitter = dynamic(() => import('../_components/widgets/MLWidgets').then(m => m.LineFitter), { ssr: false })

export default function LinearRegression() {
  return (
    <Lesson>
      <Step>
        <H>A real prediction problem</H>
        <P>
          Here are 22 houses: their <Hl c="blue">size</Hl> and the <Hl c="amber">price</Hl> they sold for. A new house comes on
          the market — 150 m². What should it cost? There’s clearly a pattern (bigger costs more), but it’s noisy: location,
          condition and luck all play a part.
        </P>
        <P>
          Our model is the simplest one there is, a straight line: <M>{'\\amber{\\hat{y}} = \\green{w}\\,\\blue{x} + \\green{b}'}</M>
          . This is called <strong>linear regression</strong> (“regression” just means predicting a number). Training it means
          finding the line that best fits the data. But what does “best” mean?
        </P>
      </Step>

      <Step gate="goal-fit">
        <H>Fit it by hand</H>
        <P>
          For each house, the <Hl c="pink">error</Hl> is the vertical gap between the dot and the line. We square each error,
          average them, and that’s the <strong>mean squared error</strong> from Module 2. The best line is the one with the
          smallest MSE.
        </P>
        <Goal id="goal-fit" task={<>Drag the two green handles to move the line. Get the mean squared error below 0.03.</>}>
          {onReach => <LineFitter goal={0.03} onGoal={onReach} />}
        </Goal>
        <Callout kind="try">
          Switch “show errors as” to <strong>squares</strong>. The MSE is literally the average area of the pink squares, so
          fitting the line means shrinking the squares. Notice how one big square dominates — squared error hates big misses.
        </Callout>
      </Step>

      <Step gate="q-mse">
        <H>Why this is “learning”</H>
        <P>You just did by hand what a training algorithm does:</P>
        <ol className="gd-list">
          <li>
            Pick a <strong>model</strong> with adjustable parameters (a line: w and b).
          </li>
          <li>
            Pick a <strong>loss</strong> that measures how wrong it is on the data (MSE).
          </li>
          <li>Adjust the parameters to make the loss as small as possible.</li>
        </ol>
        <MB>{'\\pink{L}(\\green{w}, \\green{b}) = \\frac{1}{n} \\sum_{i=1}^{n} \\big(\\green{w}\\,\\blue{x_i} + \\green{b} - \\amber{y_i}\\big)^2'}</MB>
        <P>Notice the loss is a function of the <em>parameters</em>. The data is fixed; w and b are what we’re searching over.</P>
        <Quiz
          id="q-mse"
          question="What does a mean squared error of exactly 0 mean?"
          options={[
            { label: 'The line passes through every single data point.', correct: true },
            { label: 'The model is perfect for future houses too.', why: 'Zero error on known data says nothing guaranteed about new data. (That’s the subject of “Overfitting & generalization”, later in this module.)' },
            { label: 'The line is flat.', why: 'MSE measures distance from the points, not the line’s slope.' },
          ]}
        />
      </Step>

      <Step gate="hand-fit">
        <H>Be the optimizer, in code</H>
        <Exercise
          id="hand-fit"
          title="MSE, and a hand-picked line"
          {...EX.handFit}
          hints={[
            <>
              Loop over the points: add <C>(w * xs[i] + b - ys[i]) ** 2</C>, then divide by the count.
            </>,
            <>Look at the scatter plot: what’s the slope of the cloud? Where does it cross x = 0?</>,
          ]}
        />
        <Callout kind="why">
          Guessing works for two parameters. A neural network has millions. Next lesson, we let calculus do the guessing — and
          the line will fit itself.
        </Callout>
      </Step>
    </Lesson>
  )
}
